import { FC } from 'react';
import { translate } from '../../i18n';
import { AmountInput } from '../AmountInput';
import { CVVInput } from '../CVVInput';
import { InstallmentPicker } from '../InstallmentPicker';
import { PaymentMethodCarousel } from '../PaymentMethodCarousel';
import type { PaymentMethodItem } from '../PaymentMethodCarousel/types';
import type { InstallmentPlan } from '../InstallmentSelector';
import { splitPaymentCardSectionStyles } from './styles';

export interface SplitPaymentCardSectionProps {
  cardLabel: string;
  cardOptions: PaymentMethodItem[];
  selectedCardId?: string;
  onSelectCard?: (item: PaymentMethodItem) => void;
  amount?: number;
  onAmountChange?: (value: number | undefined) => void;
  /** Las tarjetas nuevas de la sesión no piden CVV (ya va dentro de su token de pago). */
  showCvv?: boolean;
  cvv?: string;
  onCVVChange?: (value: string) => void;
  onCVVError?: (error: string | undefined) => void;
  installmentPlans: InstallmentPlan[];
  selectedInstallmentId?: string;
  onSelectInstallment?: (planId: string) => void;
  testID?: string;
  /** En la web mobile el carrusel no puede desbordar la columna, como el de una sola tarjeta. */
  contained?: boolean;
  /** Agregar, a la altura del título de la segunda tarjeta. */
  onAddPress?: () => void;
  addLabel?: string;
  onRemoveCard?: (item: PaymentMethodItem) => void;
  /** La primera tarjeta no muestra borde ni estado de selección. */
  highlightSelection?: boolean;
}

/** Línea encima de la segunda tarjeta. */
export const SplitSectionDivider: FC = () => (
  <span style={splitPaymentCardSectionStyles.dividerLine} />
);

/**
 * SplitPaymentCardSection — Bloque completo de "pagar con 2 tarjetas" para una tarjeta:
 * carrusel propio para elegir la tarjeta, monto a abonar, CVV y selector de cuotas
 * (dropdown colapsable). Cada sección elige su tarjeta de forma independiente.
 */
export const SplitPaymentCardSection: FC<SplitPaymentCardSectionProps> = ({
  cardLabel,
  cardOptions,
  selectedCardId,
  onSelectCard,
  amount,
  onAmountChange,
  showCvv = true,
  cvv,
  onCVVChange,
  onCVVError,
  installmentPlans,
  selectedInstallmentId,
  onSelectInstallment,
  testID = 'split-payment-card-section',
  contained = false,
  onAddPress,
  addLabel,
  onRemoveCard,
  highlightSelection = true,
}) => (
  <div
    style={{
      ...splitPaymentCardSectionStyles.container,
      ...(contained ? splitPaymentCardSectionStyles.contained : null),
    }}
    data-testid={testID}
  >
    <div style={splitPaymentCardSectionStyles.titleRow}>
      <h3 style={splitPaymentCardSectionStyles.title}>{cardLabel}</h3>
      {onAddPress && addLabel ? (
        <button type="button" onClick={onAddPress} style={splitPaymentCardSectionStyles.dividerAdd}>
          <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
            <rect x="0.75" y="2.75" width="10.5" height="7.5" rx="1.25" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <path d="M0.75 5.25h10.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <path d="M12.25 9.5v4.5M10 11.75h4.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          {addLabel}
        </button>
      ) : null}
    </div>
    <p style={splitPaymentCardSectionStyles.helperText}>
      {translate('paymentsUI.splitPayment.helperText')}
    </p>

    <AmountInput value={amount} onChange={onAmountChange} testID={`${testID}-amount`} />

    <div
      style={contained ? splitPaymentCardSectionStyles.carouselContained : splitPaymentCardSectionStyles.carouselContainer}
      data-testid={testID}
    >
      <PaymentMethodCarousel
        items={cardOptions}
        selectedId={selectedCardId}
        onSelect={(item) => onSelectCard?.(item)}
        onAddPress={() => undefined}
        onRemove={onRemoveCard}
        highlightSelection={highlightSelection}
        testID={`${testID}-carousel`}
        contained={contained}
      />
    </div>
    
    {selectedCardId && (
      <>
        {showCvv && (
          <CVVInput
            value={cvv}
            onChange={onCVVChange}
            onError={onCVVError}
            testID={`${testID}-cvv`}
          />
        )}

        <p style={splitPaymentCardSectionStyles.installmentTitle}>
          {translate('paymentsUI.splitPayment.installmentSectionTitle', { cardLabel })}
        </p>
        <InstallmentPicker
          plans={installmentPlans}
          selectedId={selectedInstallmentId}
          onSelect={onSelectInstallment}
          testID={`${testID}-installment-picker`}
        />
      </>
    )}
  </div>
);
